import React, { useRef, useState } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import { uploadFile, BLOG_IMAGES_BUCKET } from '../utils/storage';
import { EmbeddedImage } from '../types/blog';

interface RichTextEditorProps {
  initialValue?: string;
  onChange: (content: string, embeddedImages: EmbeddedImage[]) => void;
  placeholder?: string;
  height?: number;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  initialValue = '',
  onChange,
  placeholder = 'Start writing your post...',
  height = 500
}) => {
  const editorRef = useRef<any>(null);
  const [embeddedImages, setEmbeddedImages] = useState<EmbeddedImage[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const handleEditorChange = (content: string, editor: any) => {
    // Extract embedded images from the content
    const imageRegex = /<img[^>]+src="([^"]*)"[^>]*alt="([^"]*)"[^>]*>/g;
    const images: EmbeddedImage[] = [];
    let match;

    while ((match = imageRegex.exec(content)) !== null) {
      const [, url, alt] = match;
      if (url.includes('blog-images')) { // Only track our uploaded images
        images.push({
          url,
          alt,
        });
      }
    }

    setEmbeddedImages(images);
    onChange(content, images);
  };

  const handleImageUpload = async (blobInfo: any, progress: (percent: number) => void): Promise<string> => {
    return new Promise(async (resolve, reject) => {
      try {
        setIsUploading(true);
        
        // Convert blob to file
        const file = new File([blobInfo.blob()], blobInfo.filename(), {
          type: blobInfo.blob().type
        });

        // Upload to Supabase Storage
        const uploadedUrl = await uploadFile(file, BLOG_IMAGES_BUCKET, 'posts');
        
        setIsUploading(false);
        resolve(uploadedUrl);
      } catch (error) {
        setIsUploading(false);
        console.error('Error uploading image:', error);
        reject('Image upload failed');
      }
    });
  };

  const editorConfig = {
    height,
    menubar: false,
    plugins: [
      'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
      'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
      'insertdatetime', 'media', 'table', 'help', 'wordcount', 'emoticons'
    ],
    toolbar: [
      'undo redo | blocks | bold italic underline strikethrough | ' +
      'alignleft aligncenter alignright alignjustify | ' +
      'bullist numlist outdent indent | removeformat',
      'link image media table | forecolor backcolor | ' +
      'emoticons charmap | preview code fullscreen help'
    ].join(' | '),
    content_style: `
      body { 
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Open Sans', 'Helvetica Neue', sans-serif; 
        font-size: 16px; 
        line-height: 1.6;
        margin: 1rem;
      }
      img {
        max-width: 100%;
        height: auto;
        border-radius: 8px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        margin: 1rem 0;
      }
      blockquote {
        border-left: 4px solid #e5e7eb;
        margin: 1.5rem 0;
        padding-left: 1rem;
        font-style: italic;
        color: #6b7280;
      }
      code {
        background-color: #f3f4f6;
        padding: 0.125rem 0.25rem;
        border-radius: 0.25rem;
        font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
        font-size: 0.875em;
      }
      pre {
        background-color: #f3f4f6;
        border-radius: 0.5rem;
        padding: 1rem;
        overflow-x: auto;
      }
      table {
        border-collapse: collapse;
        width: 100%;
        margin: 1rem 0;
      }
      table, th, td {
        border: 1px solid #e5e7eb;
      }
      th, td {
        text-align: left;
        padding: 0.5rem;
      }
      th {
        background-color: #f9fafb;
        font-weight: 600;
      }
    `,
    placeholder,
    images_upload_handler: handleImageUpload,
    images_upload_base_path: '',
    images_reuse_filename: true,
    automatic_uploads: true,
    images_upload_credentials: false,
    file_picker_types: 'image',
    paste_data_images: true,
    smart_paste: true,
    
    // Enhanced image dialog
    image_advtab: true,
    image_caption: true,
    image_title: true,
    
    // Link options
    link_assume_external_targets: true,
    link_context_toolbar: true,
    
    // Table options
    table_responsive_width: true,
    table_default_attributes: {
      border: '1'
    },
    
    // Content filtering
    paste_webkit_styles: 'none',
    paste_remove_styles_if_webkit: false,
    
    // Performance
    skin: 'oxide',
    theme: 'silver',
    
    // Accessibility
    a11y_advanced_options: true,
    
    init_instance_callback: (editor: any) => {
      editorRef.current = editor;
    }
  };

  return (
    <div className="rich-text-editor">
      {isUploading && (
        <div className="mb-4 p-3 bg-blue-100 border border-blue-400 text-blue-700 rounded">
          Uploading image...
        </div>
      )}
      
      <Editor
        tinymceScriptSrc="/tinymce/tinymce.min.js"
        initialValue={initialValue}
        init={editorConfig}
        onEditorChange={handleEditorChange}
      />
      
      {embeddedImages.length > 0 && (
        <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded">
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            Embedded Images ({embeddedImages.length})
          </h4>
          <div className="text-xs text-gray-600 space-y-1">
            {embeddedImages.map((img, index) => (
              <div key={index} className="truncate">
                {img.alt || `Image ${index + 1}`}: {img.url.split('/').pop()}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default RichTextEditor;